import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Magento2_CartItemInput } from '@__generated__/globalTypes';
import { Button } from '@app/components/Button';
import { useGetCartId } from '@app/context/CartIdProvider/CartIdProvider';
import useAddProductsToCart from '@app/hooks/useAddProductsToCart';
import { useRemoveCartItem } from '@app/hooks/useRemoveCartItem';
import useSimpleCart from '@app/hooks/useSimpleCart';

import messages from './QuickAddToCartButton.messages';

const QuickAddToCartButton = ({ sku }: { sku: string }) => {
  const [loading, setLoading] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const { loading: cartIdLoading } = useGetCartId();
  const { removeItem, loading: removeItemLoading } = useRemoveCartItem();
  const { addItems, addProductsLoading } = useAddProductsToCart();
  const { items, loading: cartLoadingStatus } = useSimpleCart();

  const cartLoading =
    cartIdLoading ||
    cartLoadingStatus ||
    addProductsLoading ||
    removeItemLoading;

  const getCartProduct = useCallback(() => {
    return items?.find((item) => item?.product.sku === sku);
  }, [items, sku]);

  useEffect(() => {
    if (!cartLoading) {
      setLoading(false);
    }

    if (typeof getCartProduct() !== 'undefined') {
      setAddedToCart(true);
    } else {
      setAddedToCart(false);
    }
  }, [cartLoading, getCartProduct, items, sku]);

  const addToCart = useCallback(() => {
    const cartItem: Magento2_CartItemInput = {
      sku,
      quantity: 1,
    };

    setLoading(true);
    addItems([cartItem]);
  }, [addItems, sku]);

  const removeFromCart = useCallback(() => {
    const uid = getCartProduct()?.uid;
    if (uid) {
      setLoading(true);
      removeItem(uid);
    }
  }, [getCartProduct, removeItem]);

  const onButtonClick = () => {
    if (cartLoading) return;

    if (addedToCart) {
      removeFromCart();
    } else {
      addToCart();
    }
  };

  return (
    <Button
      isLoading={loading}
      disabled={loading}
      type={addedToCart ? 'secondary' : 'primary'}
      onClick={onButtonClick}
      fullWidth
    >
      {!addedToCart && !loading && <FormattedMessage {...messages.add} />}
      {addedToCart && !loading && <FormattedMessage {...messages.remove} />}
    </Button>
  );
};

export default QuickAddToCartButton;
